<template>
  <div class="sign-in-container">
    <div v-if="loading" class="overlay">
      <loading-icon></loading-icon>
    </div>
    <div v-else class="sign-in-wrapper">
      <div class="current-step-wrapper" v-for="step in steps" :key="step.name">
        <div v-if="currentStep === step.name">
          <login-step
              :linkLabel="step.linkLabel"
              :linkText="step.linkText"
              :stepLabel="step.stepLabel"
              @linkClicked="linkAction"
              @submit="submitAction">
            <form-input
                v-if="step.input.type === 'phone'"
                slot="input"
                :autocomplete="step.input.autocomplete"
                :fieldToUpdate="step.input.fieldToUpdate"
                :id="step.input.id"
                @updateInput="updateData">
            </form-input>
            <number-input
                v-else
                slot="input"
                :autocomplete="step.input.autocomplete"
                :fieldToUpdate="step.input.fieldToUpdate"
                :id="step.input.id"
                :inputLabel="step.input.inputLabel"
                :requiredLength="step.input.requiredLength"
                :type="step.input.type"
                @updateNumber="updateData">
            </number-input>
            <div v-if="loyalty" class="verbiage-wrapper" slot="verbiage">
              <label v-for="(line, index) in step.verbiages" :key="index" class="verbiage-label">
                {{line}}
              </label>
            </div>
            <div slot="graphics" class="login-graphics">
              <img v-if="step.name === 'enter-phone'" class="graphics-img" />
              <div class="links-wrapper">
                <div v-if="appStoreUrl" class="app-store-div">
                  <a :href="appStoreUrl" target="_blank">
                    <img :src="appStoreImage" alt="image" class="app-store-img" />
                  </a>
                </div>
                <div v-if="playStoreUrl" class="app-store-div">
                  <a :href="playStoreUrl" target="_blank">
                    <img :src="playStoreImage" alt="image" class="app-store-img" />
                  </a>
                </div>
              </div>
            </div>
          </login-step>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import AxiosMixin from '@/mixins/AxiosMixin'
  import LoadingIcon from '@/components/Shared/LoadingIcon'
  import LoginStep from '@/components/Login/LoginStep'
  import NumberInput from '@/components/Shared/NumberInput'
  import FormInput from '@/components/Shared/FormInput'
  import appStoreImage from '@/assets/images/download-on-the-app-store.png'
  import playStoreImage from '@/assets/images/get-it-on-google-play.png'
  import config from "../../config";

  export default {
    props: {
      baseUrl: String,
      joinLink: String,
      loyalty: Boolean,
      appStoreUrl: String,
      playStoreUrl: String,
    },

    name: 'SignIn',

    components: {
      LoadingIcon,
      LoginStep,
      NumberInput,
      FormInput,
    },

    mixins: [AxiosMixin],

    data() {
      return {
        currentStep: 'enter-phone',
        loading: false,
        password: null,
        phoneNumber: null,
        steps: [],
        appStoreImage,
        playStoreImage,
      }
    },

    methods: {
      afterSubmitPasswordError(error) {
        if (error.response?.status === 401) {
          this.$emit('setPageAlert', 'invalid PIN')
          this.password = null

          this.loading = false
        } else {
          this.$emit('setErrors', error)
        }
      },

      afterSubmitPasswordSuccess(response) {
        const data = response?.data,
            signatureNeeded = data?.signature_needed

        if (data && !signatureNeeded) {
          this.redirectTo(data?.wallet_url)
        } else if (data && signatureNeeded) {
          this.$emit('updateData', 'allowedSmsStored', data?.allowed_sms)
          this.$emit('updateData', 'type', 'signature-capture')
          this.$emit('setPageAlert')
        } else {
          this.$emit('setErrors')
        }
      },

      afterSubmitPhoneError(error) {
        if (error.response?.status === 404) {
          // account not found, send them to join form
          this.redirectToJoinForm()
        } else {
          this.$emit('setErrors', error)
        }
      },

      autoConnect() {
        // this fills out the mobile number automatically when the member joined through the join form sharing link flow

        const urlParams = location.search.substr(1).split('&')
        let parsedParams = urlParams.reduce((acc, param) => {
          let splitParams = param.split('=')
          acc[splitParams[0]] = unescape(splitParams[1])
          return acc
        }, {})

        this.phoneNumber = parsedParams['phone']
        if (this.phoneNumber) this.submitPhone()
      },

      afterSubmitPhoneSuccess(response) {
        const data = response.data,
              noError = data?.wallet_url || data?.setup_complete || (data?.setup_complete === false)
        noError && this.updateMember()

        if (data?.wallet_url) {
          this.redirectTo(data?.wallet_url)
        } else if (data?.setup_complete) {
          this.currentStep = 'enter-password'
          this.loading = false
          this.$emit('setPageAlert')
        } else if (data?.setup_complete === false) {
          this.resetPassword()
        } else if (data?.member_id) {
          const redirectUrl = config.addUrl + data?.add_url.replace(this.phoneNumber, `${data?.member_id}`)
          this.loading = false
          window.location.href = redirectUrl
        } else {
          this.$emit('setErrors')
        }
      },

      apiUrl(checkPassword=true) {
        return `${this.baseUrl}/${checkPassword ? 'member_passwords' : 'members'}/${this.phoneNumber}`
      },

      configureSteps() {
        this.steps = [
          {
            name: 'enter-phone',
            stepLabel: this.phoneStepLabel,
            input: {
              autocomplete: "home tel",
              fieldToUpdate: "phoneNumber",
              id: "phone_number",
              type: 'phone'
            },
            verbiages: [
              'already a member?',
              'enter your phone number or email to retrieve your wallet'
            ]
          },
          {
            linkLabel: "forgot your log in code?",
            linkText: "set up new log in code",
            name: 'enter-password',
            stepLabel: "please enter your log in code to access your wallet",
            input: {
              fieldToUpdate: "password",
              id: "password",
              requiredLength: 4,
              type: 'password',
            }
          },
        ]
      },

      linkAction() {
        this.isPhoneStep ? this.redirectToJoinForm() : this.resetPassword()
      },

      redirectTo(redirectUrl) {
        // this method is used for subscription sharing links routing

        if (location.search) {
          const urlParams = location.search.substr(1).split('&')
          let parsedParams = urlParams.reduce((acc, param) => {
            let splitParams = param.split('=')
            acc[splitParams[0]] = unescape(splitParams[1])
            return acc
          }, {})

          // populate params for sharing links
          if (parsedParams['plan']) {
            redirectUrl = redirectUrl.replace(/\?/, `?plan=${parsedParams['plan']}&`)
          }
          if (parsedParams['link']) {
            redirectUrl = redirectUrl.replace(/\?/, `?link=${parsedParams['link']}&`)
          }
          if (parsedParams['route']) {
            redirectUrl = redirectUrl.replace(/\/profile/, `${parsedParams['route']}`)
          }

        }
        this.loading = false
        window.location.href = redirectUrl
      },

      redirectToJoinForm() {
        this.loading = false
        window.location.href = this.joinLink
      },

      resetPassword() {
        this.loading = true
        this.$emit('updateData', 'type', 'reset-password')
        this.$emit('setPageAlert')
      },

      submitAction() {
        this.isPhoneStep ? this.submitPhone() : this.submitPassword()
      },

      submitPassword() {
        if (!this.password) { this.$emit('setPageAlert', 'must enter valid PIN'); return }

        this.loading = true
        this.axiosMethod({
          url: `${this.apiUrl()}/verify?password=${this.password}`,
          onSuccess: this.afterSubmitPasswordSuccess,
          onError: this.afterSubmitPasswordError
        })
      },

      submitPhone() {
        if (!this.phoneNumber) { this.$emit('setPageAlert', 'must enter valid phone number or email'); return }
        this.$emit('updateData', 'phoneNumber', this.phoneNumber)
        this.loading = true
        this.axiosMethod({
          url: `${this.apiUrl()}`,
          onSuccess: this.afterSubmitPhoneSuccess,
          onError: this.afterSubmitPhoneError
        })
      },

      updateData(field, value) {
        this[field] = value
      },

      updateMember() {
        const re = /(?<context>location|custom_group)=(?<value>[^&]+)/,
          match = this.joinLink.match(re)?.groups,
          params = {}

        if (match) { params[match["context"]] = match["value"] }

        this.axiosMethod({
          methodType: 'patch',
          url: this.apiUrl(false),
          params: params,
          onError: this.updateMemberOnError
        })
      },

      updateMemberOnError(error) {
        this.$emit('setErrors', error)
      },
    },

    created() {
      this.configureSteps()
      this.autoConnect()
    },

    computed: {
      isPhoneStep() {
        return this.currentStep === 'enter-phone'
      },

      phoneStepLabel() {
        const baseVerbiage = 'enter your phone number to enroll in the program so you can start',
            loyaltyEnding = 'earning and redeeming awesome rewards!',
            nonLoyaltyEnding = 'receiving important updates and exclusive content',
            ending = this.loyalty ? loyaltyEnding : nonLoyaltyEnding
        return `${baseVerbiage} ${ending}`
      }
    }
  }
</script>

<style lang="sass">
.login-graphics
  left: 0
  margin: 0 5%
  position: absolute
  width: 90%

.verbiage-wrapper
  display: flex
  flex-flow: column wrap
  margin: 1.5rem auto 1rem auto

.links-wrapper
  display: flex
  flex-direction: row
  flex-wrap: wrap
  justify-content: center
  overflow-x: auto
  margin-top: 2%

  .app-store-div
    height: 100%
    margin: 2px

    .app-store-img
      width: 100%
      height: 100%

@media screen and (max-width: 1023px)
  .graphics-img
    content: url('~@/assets/images/customer_login_mobile.jpg')
    width: 100%
    height: 100%

  .app-store-div
    width: 48%

@media screen and (min-width: 1024px)
  .graphics-img
    content: url('~@/assets/images/customer_login_desktop.jpg')
    width: 100%
    height: 100%

  .links-wrapper
    justify-content: center

  .app-store-div
    width: 25%
</style>
